'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';
import { AFS_CONFIG, AFS_SCRIPT_URL } from '@/app/constants';

type GoogCsaAction = 'ads' | 'relatedsearch';
type GoogCsaOptions = Record<string, unknown>;

type GoogCsaFn = (action: GoogCsaAction, pageOptions: GoogCsaOptions, ...unitOptions: GoogCsaOptions[]) => void;

type GoogCsaQueue = GoogCsaFn & {
  q?: unknown[][];
  t?: number;
};

declare global {
  interface Window {
    _googCsa?: GoogCsaQueue;
  }
}

export interface AdSenseCsaKeepaliveProps {
  /** 容器 id；同一页面不应重复 */
  containerId?: string;
  /** 相关搜索词条数 */
  relatedSearches?: number;
  /** 语言代码（对应 AFS 的 hl 参数） */
  hl?: string;
  /** 容器 className，便于宿主页面控制外观 */
  className?: string;
}

/**
 * AdSense 自定义搜索广告（AFS / CSA）——"内容页相关搜索"保活单元
 *
 * - 目的：让配置的 pubId 产生广告请求，用于维持 AdSense 账号活跃（保活资质）
 * - 合规：容器默认展示，无广告时在 adLoadedCallback 里隐藏；未传 query；不重复调用
 * - 限制：同一页面不应挂载多次（AFS 禁止单页多次 _googCsa 调用）
 */
export const AdSenseCsaKeepalive = ({
  containerId = 'afs-keepalive-container',
  relatedSearches = 6,
  hl = 'en',
  className,
}: AdSenseCsaKeepaliveProps) => {
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current || typeof window === 'undefined') return;
    requestedRef.current = true;

    if (!window._googCsa) {
      const queue = ((...args: unknown[]) => {
        queue.q = queue.q ?? [];
        queue.q.push(args);
      }) as GoogCsaQueue;
      queue.t = Date.now();
      window._googCsa = queue;
    }

    window._googCsa(
      'relatedsearch',
      {
        pubId: AFS_CONFIG.PUB_ID,
        styleId: AFS_CONFIG.STYLE_ID,
        relatedSearchTargeting: 'content',
        resultsPageBaseUrl: `${window.location.origin}/blog`,
        resultsPageQueryParam: 'q',
        hl,
      },
      {
        container: containerId,
        relatedSearches,
        width: '100%',
        adLoadedCallback: (name: string, adsLoaded: boolean) => {
          if (!adsLoaded) {
            const node = document.getElementById(name);
            if (node) node.style.display = 'none';
          }
        },
      },
    );
  }, [containerId, relatedSearches, hl]);

  return (
    <>
      <Script src={AFS_SCRIPT_URL} strategy="lazyOnload" />
      <div id={containerId} className={className ?? 'w-full'} />
    </>
  );
};
